import React from 'react';

import styled from 'react-emotion';

import { Query } from 'react-apollo';
import blogPosts from './BlogPostsQuery.gql';

import CategoryLink from '@jetshop/ui/CategoryLink';
import Image from '@jetshop/ui/Image';
import theme from '@jetshop/ui/utils/theme';
import t from '@jetshop/intl';

const Truncate = styled('div')`
  position: relative;
  max-height: 8rem;
  overflow: hidden;
  ::after {
    content: '';
    display: block;
    width: 100%;
    position: absolute;
    padding: 2rem;
    bottom: -1px;
    left: 0;
    background: -moz-linear-gradient(
      180deg,
      rgba(248, 248, 248, 0) 0%,
      rgba(248, 248, 248, 1) 82%
    );
    background: -webkit-linear-gradient(
      180deg,
      rgba(248, 248, 248, 0) 0%,
      rgba(248, 248, 248, 1) 82%
    );
    background: linear-gradient(
      180deg,
      rgba(248, 248, 248, 0) 0%,
      rgba(248, 248, 248, 1) 82%
    );
    filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#020024",endColorstr="#f8f8f8",GradientType=1);
  }
`;

const Container = styled('div')`
  display: flex;
  flex-flow: wrap;
  width: 100%;
  margin-bottom: 1rem;
`;

const Category = styled('div')`
  display: flex;
  align-items: stretch;
  margin-bottom: 3%;

  ${theme('above.md')} {
    ${p => p.theme.calculateWidth(3, 3)};
  }

  ${theme('only.sm')} {
    ${p => p.theme.calculateWidth(2, 3)};
  }

  ${theme('below.sm')} {
    width: 100%;
  }
`;

const CategoryImage = styled(Image)``;

const ImageWrapper = styled('div')`
  ${CategoryImage} {
    background-position: center center;
    background-size: contain;
    padding-bottom: 58% !important;
  }
`;

const StyledWrapper = styled('div')`
  display: flex;
  width: 100%;
  flex-grow: 1;
  flex-flow: column;
  text-decoration: none;
  background-color: ${theme('colors.blog.background')};
`;

const StyledCategoryLink = styled('div')`
  display: flex;
  width: 100%;
  text-decoration: none;
  font-weight: bold;
  color: ${theme('colors.primary')};
`;

const UnStyledCategoryLink = styled(CategoryLink)`
  text-decoration: none;
  display: flex;
  flex-flow: column;
  align-items: stretch;
  align-self: stretch;
  flex: 1 1 auto;
`;

const CategoryName = styled('div')`
  flex-grow: 1;
  align-self: stretch;
  font-size: 1.2rem;
  font-weight: bold;
  color: ${theme('colors.blog.categoryName')};
  margin-bottom: 0rem;
  line-height: 1.2;
  letter-spacing: 1px;
`;

const CategoryParentName = styled('div')`
  font-size: 0.85rem;
  color: ${theme('colors.blog.categoryParentName')};
  text-transform: none;
  font-weight: normal;
  margin-bottom: 0.5rem;
  margin-top: 0.1rem;
  font-style: italic;
`;

const CategoryContentWrap = styled('div')`
  color: ${theme('colors.blog.content')};
  padding: 1.5rem;
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-flow: column;
  p {
    padding: 0;
    display: block;
    :nth-child(n + 3) {
      display: none;
    }
    img {
      display: none;
    }
    strong {
      font-weight: 400;
    }
  }
`;

const CategoryContent = styled('div')`
  overflow: hidden;
  position: relative;
  flex: 1;
`;

const imageSizes = [1 / 2, 1 / 2, 1 / 4, 1 / 3];

const Posts = ({ category }) => {
  const queryParams = {
    root: category.id,
    levels: 4
  };

  return (
    <Query query={blogPosts} variables={queryParams} key={category.id}>
      {({ loading, error, data }) => {
        if (loading) {
          return null;
        }

        if (error) {
          return null;
        }

        const filteredCategories = data.categories.filter(
          category => category.images && category.images.length > 0
        );

        return (
          <Container>
            {filteredCategories.map((subCategory, index) => (
              <Category key={index}>
                <StyledWrapper>
                  <UnStyledCategoryLink category={subCategory}>
                    <ImageWrapper>
                      {subCategory.images[3] && (
                        <CategoryImage
                          src={subCategory.images[3].url}
                          sizes={imageSizes}
                          aspect={'4:3|3:4'}
                        />
                      )}
                    </ImageWrapper>
                    <CategoryContentWrap>
                      <CategoryName>{subCategory.name}</CategoryName>
                      <CategoryParentName>
                        {subCategory.parent && subCategory.parent.name}
                      </CategoryParentName>
                      <Truncate clamp={4}>
                        <CategoryContent
                          data-testid="category-description"
                          dangerouslySetInnerHTML={{
                            __html: subCategory.content
                          }}
                        />
                      </Truncate>
                      <StyledCategoryLink category={subCategory}>
                        {t('Read more')}
                      </StyledCategoryLink>
                    </CategoryContentWrap>
                  </UnStyledCategoryLink>
                </StyledWrapper>
              </Category>
            ))}
          </Container>
        );
      }}
    </Query>
  );
};

export default Posts;
